"use client";

import { useId, useState } from "react";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { useProjectText } from "@/features/project/useProjectText";
import { cn } from "@/lib/cn";
import type { SecondaryLayerSection } from "@/types";
import { defaultOpenSectionIds, useSecondaryLayers } from "../useSecondaryLayers";
import { SecondaryLayerRow } from "./SecondaryLayerRow";

/** The overlays a project offers, grouped into collapsible sections. */
export function SecondaryLayers() {
  const { sections } = useSecondaryLayers();

  // Seeded from the project's own `defaultOpen`, in a lazy initialiser rather
  // than an effect: an effect would render one frame with everything collapsed
  // and then expand, which reads as the panel twitching on open.
  const [openIds, setOpenIds] = useState(() => defaultOpenSectionIds(sections));

  const toggleOpen = (id: string) =>
    setOpenIds((open) =>
      open.includes(id) ? open.filter((entry) => entry !== id) : [...open, id]
    );

  // By id, so the order doesn't depend on how the CMS happened to return them.
  const ordered = [...sections].sort((a, b) =>
    (a.id ?? "").localeCompare(b.id ?? "")
  );

  return (
    <div className="ml-2 space-y-4 tablet:ml-0">
      {ordered.map((section, index) => (
        <Section
          key={section.id ?? index}
          section={section}
          isOpen={!!section.id && openIds.includes(section.id)}
          onToggleOpen={() => section.id && toggleOpen(section.id)}
          isLast={index === ordered.length - 1}
        />
      ))}
    </div>
  );
}

function Section({
  section,
  isOpen,
  onToggleOpen,
  isLast,
}: {
  section: SecondaryLayerSection;
  isOpen: boolean;
  onToggleOpen: () => void;
  isLast: boolean;
}) {
  const projectText = useProjectText();
  const { isSectionActive, toggleSection } = useSecondaryLayers();
  const contentId = useId();

  const title = projectText(
    `map.layers.sections.section_${section.id}`,
    section.id ?? ""
  );

  return (
    <div className={cn("border-b", isLast ? "border-transparent" : "border-black/10")}>
      {/* The expander and the section switch are siblings, not nested. Legacy put
          the switch inside the expander button and stopped the event from
          propagating — a button inside a button, which is invalid markup and
          which assistive technology cannot operate. */}
      <div className="flex items-center justify-between pb-5">
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={isOpen}
          aria-controls={contentId}
          className="flex flex-1 items-center justify-between text-left"
        >
          <span className="text-sm font-bold text-black dark:text-off-white tablet:text-base">
            {title}
          </span>
        </button>

        <div className="flex items-center gap-2">
          {section.hasToggle && (
            <ToggleSwitch
              isActive={isSectionActive(section)}
              onChange={() => toggleSection(section)}
              label={title}
            />
          )}

          <button
            type="button"
            onClick={onToggleOpen}
            aria-hidden
            tabIndex={-1}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-xl border border-[#F1F1F1]",
              "transform transition-transform duration-300",
              isOpen ? "rotate-180" : "rotate-0"
            )}
          >
            <i className="icon ic-material_ic_keyboard_arrow_down_24px text-xl" />
          </button>
        </div>
      </div>

      <div
        id={contentId}
        inert={!isOpen}
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="space-y-4 pb-5 pl-5">
          {(section.secondaryLayers ?? []).map((layer, index) => (
            <SecondaryLayerRow key={layer.name ?? index} layer={layer} />
          ))}
        </div>
      </div>
    </div>
  );
}
